import React, { useEffect, useRef } from 'react';
import { useTranslations } from 'react-ui';

interface MapContextMenuProps {
  /** Screen position (x, y) */
  position: { x: number; y: number };
  /** Geographic location */
  location: { lng: number; lat: number };
  /** Resolved address (if available) */
  address?: string;
  /** Loading state for address resolution */
  loading: boolean;
  /** Callback when "Create Balloon" is clicked */
  onCreateEvent: () => void;
  /** Callback to close the menu */
  onClose: () => void;
}

/**
 * Context menu that appears on right-click (desktop) or long-press (mobile).
 * Displays location information and provides quick access to event creation.
 */
export const MapContextMenu: React.FC<MapContextMenuProps> = ({
  position,
  location,
  address,
  loading,
  onCreateEvent,
  onClose,
}) => {
  const menuRef = useRef<HTMLDivElement>(null);
  const { translated } = useTranslations();

  // Calculate smart positioning to stay within viewport
  const [adjustedPosition, setAdjustedPosition] = React.useState(position);

  useEffect(() => {
    if (!menuRef.current) return;

    const menu = menuRef.current;
    const menuRect = menu.getBoundingClientRect();
    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;
    const padding = 16;

    let x = position.x;
    let y = position.y;

    // Flip left if overflowing right edge
    if (x + menuRect.width + padding > viewportWidth) {
      x = position.x - menuRect.width;
    }

    // Flip up if overflowing bottom edge
    if (y + menuRect.height + padding > viewportHeight) {
      y = position.y - menuRect.height;
    }

    // Ensure minimum padding from edges
    x = Math.max(padding, Math.min(x, viewportWidth - menuRect.width - padding));
    y = Math.max(padding, Math.min(y, viewportHeight - menuRect.height - padding));

    setAdjustedPosition({ x, y });
  }, [position]);

  // Close on Escape key
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [onClose]);

  // Close when clicking outside
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        onClose();
      }
    };

    // Small delay to prevent immediate close from the same click that opened the menu
    const timer = setTimeout(() => {
      document.addEventListener('mousedown', handleClickOutside);
      document.addEventListener('touchstart', handleClickOutside as any);
    }, 100);

    return () => {
      clearTimeout(timer);
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('touchstart', handleClickOutside as any);
    };
  }, [onClose]);

  // Format coordinates
  const formatCoordinate = (value: number, precision = 6) => {
    return value.toFixed(precision);
  };

  return (
    <>
      {/* Backdrop for mobile */}
      <div
        className="be-context-menu-backdrop"
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.1)',
          zIndex: 9998,
        }}
        onClick={onClose}
      />

      {/* Menu */}
      <div
        ref={menuRef}
        className="be-context-menu"
        style={{
          position: 'fixed',
          top: adjustedPosition.y,
          left: adjustedPosition.x,
          width: '300px',
          backgroundColor: '#FFFFFF',
          borderRadius: '12px',
          boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
          border: '1px solid #E5E7EB',
          zIndex: 9999,
          overflow: 'hidden',
          animation: 'be-context-menu-enter 150ms ease-out',
        }}
      >
        {/* Address Section */}
        <div
          style={{
            padding: '16px',
            borderBottom: '1px solid #E5E7EB',
          }}
        >
          {loading ? (
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                color: '#6B7280',
                fontSize: '14px',
              }}
            >
              <svg
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                style={{ animation: 'be-spinner-rotate 1s linear infinite' }}
              >
                <circle cx="12" cy="12" r="10" strokeWidth="3" opacity="0.25" />
                <path
                  d="M12 2a10 10 0 0 1 10 10"
                  strokeWidth="3"
                  strokeLinecap="round"
                />
              </svg>
              {translated('contextMenu.loadingAddress', 'Loading address...')}
            </div>
          ) : address ? (
            <div>
              <div
                style={{
                  fontSize: '14px',
                  fontWeight: 500,
                  color: '#1F2937',
                  lineHeight: '1.5',
                  whiteSpace: 'pre-line',
                }}
              >
                {address}
              </div>
            </div>
          ) : (
            <div
              style={{
                fontSize: '14px',
                color: '#6B7280',
              }}
            >
              {translated('contextMenu.unknownLocation', 'Unknown Location')}
            </div>
          )}

          {/* Coordinates */}
          <div
            style={{
              marginTop: '8px',
              fontSize: '12px',
              color: '#6B7280',
              fontFamily: 'monospace',
            }}
          >
            🌐 {formatCoordinate(location.lng)}, {formatCoordinate(location.lat)}
          </div>
        </div>

        {/* Action Section */}
        <div style={{ padding: '8px' }}>
          <button
            onClick={onCreateEvent}
            style={{
              width: '100%',
              height: '48px',
              backgroundColor: '#BF2088',
              color: '#FFFFFF',
              border: 'none',
              borderRadius: '8px',
              fontSize: '14px',
              fontWeight: 500,
              cursor: 'pointer',
              transition: 'background-color 0.2s ease',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '8px',
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = '#8B1763';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = '#BF2088';
            }}
          >
            <span style={{ fontSize: '18px' }}>➕</span>
            {translated('contextMenu.createBalloon', 'Create Balloon')}
          </button>
        </div>
      </div>

      {/* Animations */}
      <style>{`
        @keyframes be-context-menu-enter {
          from {
            opacity: 0;
            transform: scale(0.95);
          }
          to {
            opacity: 1;
            transform: scale(1);
          }
        }

        @keyframes be-spinner-rotate {
          from {
            transform: rotate(0deg);
          }
          to {
            transform: rotate(360deg);
          }
        }

        /* Mobile responsive */
        @media (max-width: 375px) {
          .be-context-menu {
            width: 280px !important;
          }
        }
      `}</style>
    </>
  );
};
